import { FC, useEffect, useState } from 'react';

import { Box, BoxProps, FormLabel, InputBase, Typography } from '@mui/material';
import { debounce } from '@npi/buddy-utils';
import { FormikErrors } from 'formik';
import { NumberFormatValues, NumericFormat } from 'react-number-format';

import { useAuthContext } from 'src/contexts/AuthProvider';
import './index.css';

interface CustomCurrencyInput {
    label: string;
    name: string;
    currencySymbol: string;
    amount: number;
    onNumberChange: (
        field: string,
        value: any,
        shouldValidate?: boolean
    ) => Promise<void | FormikErrors<object>>;
    onCurrencyChange?: (number) => void;
    placeholder?: string;
    error?: string;
    boxStyling?: BoxProps;
    dataTestId?: string;
}

export const CustomCurrencyInput: FC<CustomCurrencyInput> = ({
    name,
    label,
    amount,
    onNumberChange,
    placeholder,
    boxStyling,
    dataTestId = `${name}-currency-input`,
}) => {
    const { usersCurrency } = useAuthContext();
    const [currencyAmount, setCurrencyAmount] = useState(amount);

    useEffect(() => {
        if (!currencyAmount) {
            return;
        }
        const debouncedFunction = debounce(() => {
            onNumberChange('pi_creditLimit', currencyAmount);
        }, 1000);

        debouncedFunction();

        return () => {
            return debouncedFunction.cancel();
        };
    }, [currencyAmount]);

    return (
        <Box display="flex" flexDirection="column" flex={1} {...boxStyling}>
            <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                {label}
            </FormLabel>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                flex={1}
                height="56px"
                borderRadius="8px"
                overflow="hidden"
                border="1px solid #F39E4E"
            >
                <Box
                    height="100%"
                    bgcolor="#F39E4E"
                    width="20%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                >
                    <Typography
                        fontSize="16px"
                        lineHeight="24px"
                        color="white"
                        fontWeight="600"
                        textAlign="center"
                    >
                        {usersCurrency}
                    </Typography>
                </Box>
                <Box height="100%" width="100%" display="flex">
                    <NumericFormat
                        inputProps={{
                            'data-testid': dataTestId,
                        }}
                        defaultValue={0.0}
                        value={currencyAmount}
                        decimalSeparator="."
                        decimalScale={2}
                        fixedDecimalScale
                        style={{ width: '100%', borderWidth: '0px', padding: '16px 14px' }}
                        thousandSeparator
                        onValueChange={(values: NumberFormatValues) => {
                            setCurrencyAmount(Number.parseFloat(values.value));
                        }}
                        customInput={InputBase}
                        placeholder={placeholder}
                        autoComplete="off"
                    />
                </Box>
            </Box>
        </Box>
    );
};
